import { useState } from 'react';
import { MapPin, Users, ArrowRight, Sparkles } from 'lucide-react';
import { useLocation, LOCATIONS, GUEST_TYPES } from '../context/LocationContext';
import { useLanguage } from '../context/LanguageContext';

export default function OnboardingScreen() {
  const [step, setStep] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedGuestType, setSelectedGuestType] = useState(null);
  const { updateLocation, updateGuestType, completeOnboarding } = useLocation();
  const { language, t } = useLanguage();

  const translations = {
    en: {
      welcome: "Welcome to",
      tagline: "Your hyper-local concierge for Puglia",
      whereStaying: "Where are you staying?",
      selectLocation: "This helps us show you only the best nearby",
      whatTrip: "What type of trip is this?",
      personalizeExp: "We'll personalize your recommendations",
      continue: "Continue",
      letsGo: "Let's explore",
      monopoliDesc: "Lively beaches, boats & local flavors",
      polignanoDesc: "Dramatic cliffs, cliff diving & iconic views",
      fasanoDesc: "Luxury masserie, chefs & exclusive clubs"
    },
    it: {
      welcome: "Benvenuto su",
      tagline: "Il tuo concierge iper-locale per la Puglia",
      whereStaying: "Dove alloggi?",
      selectLocation: "Questo ci aiuta a mostrarti solo il meglio nelle vicinanze",
      whatTrip: "Che tipo di viaggio è questo?",
      personalizeExp: "Personalizzeremo i tuoi consigli",
      continue: "Continua",
      letsGo: "Esploriamo",
      monopoliDesc: "Spiagge vivaci, barche e sapori locali",
      polignanoDesc: "Scogliere spettacolari, tuffi e viste iconiche",
      fasanoDesc: "Masserie di lusso, chef e club esclusivi"
    },
    fr: {
      welcome: "Bienvenue sur",
      tagline: "Votre concierge hyper-local pour les Pouilles",
      whereStaying: "Où séjournez-vous?",
      selectLocation: "Cela nous aide à vous montrer uniquement le meilleur à proximité",
      whatTrip: "Quel type de voyage est-ce?",
      personalizeExp: "Nous personnaliserons vos recommandations",
      continue: "Continuer",
      letsGo: "Explorons",
      monopoliDesc: "Plages animées, bateaux et saveurs locales",
      polignanoDesc: "Falaises dramatiques, plongeons et vues iconiques",
      fasanoDesc: "Masserie de luxe, chefs et clubs exclusifs"
    },
    de: {
      welcome: "Willkommen bei",
      tagline: "Ihr hyper-lokaler Concierge für Apulien",
      whereStaying: "Wo übernachten Sie?",
      selectLocation: "Das hilft uns, Ihnen nur das Beste in der Nähe zu zeigen",
      whatTrip: "Was für eine Reise ist das?",
      personalizeExp: "Wir personalisieren Ihre Empfehlungen",
      continue: "Weiter",
      letsGo: "Los geht's",
      monopoliDesc: "Lebhafte Strände, Boote und lokale Aromen",
      polignanoDesc: "Dramatische Klippen, Klippenspringen und ikonische Ausblicke",
      fasanoDesc: "Luxus-Masserie, Köche und exklusive Clubs"
    },
    es: {
      welcome: "Bienvenido a",
      tagline: "Tu conserje hiperlocal para Puglia",
      whereStaying: "¿Dónde te alojas?",
      selectLocation: "Esto nos ayuda a mostrarte solo lo mejor cerca",
      whatTrip: "¿Qué tipo de viaje es este?",
      personalizeExp: "Personalizaremos tus recomendaciones",
      continue: "Continuar",
      letsGo: "Exploremos",
      monopoliDesc: "Playas animadas, barcos y sabores locales",
      polignanoDesc: "Acantilados dramáticos, saltos y vistas icónicas",
      fasanoDesc: "Masserías de lujo, chefs y clubs exclusivos"
    }
  };

  const txt = translations[language] || translations.en;

  const handleContinue = () => {
    if (step === 1 && selectedLocation) {
      updateLocation(selectedLocation);
      setStep(2);
    } else if (step === 2 && selectedGuestType) {
      updateGuestType(selectedGuestType);
      completeOnboarding();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-300 via-sand-200 to-olive-100 flex flex-col" data-testid="onboarding-screen">
      {/* Header - matching HomePage style */}
      <div className="pt-16 pb-8 px-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-olive-500 to-olive-600 rounded-full flex items-center justify-center shadow-lg shadow-olive-600/30">
            <span className="text-white font-bold text-xl">O</span>
          </div>
        </div>
        <p className="text-olive-600 text-xs tracking-[0.2em] font-semibold uppercase mb-2">{txt.welcome}</p>
        <h1 className="text-4xl font-serif text-stone-800 mb-3">OspitAI</h1>
        <p className="text-stone-500 text-sm">{txt.tagline}</p>
      </div>

      {/* Step Indicator */}
      <div className="flex justify-center gap-2 px-6 mb-8">
        <div className={`h-1 w-16 rounded-full transition-colors ${step >= 1 ? 'bg-olive-500' : 'bg-sand-400'}`} />
        <div className={`h-1 w-16 rounded-full transition-colors ${step >= 2 ? 'bg-olive-500' : 'bg-sand-400'}`} />
      </div>

      {/* Content */}
      <div className="flex-1 px-6 pb-8">
        {step === 1 && (
          <div className="max-w-md mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <MapPin className="text-olive-600" size={24} />
              <h2 className="text-2xl font-serif text-stone-800">{txt.whereStaying}</h2>
            </div>
            <p className="text-stone-500 text-sm mb-8">{txt.selectLocation}</p>

            <div className="space-y-4">
              {Object.values(LOCATIONS).map((loc) => (
                <button
                  key={loc.id}
                  onClick={() => setSelectedLocation(loc.id)}
                  className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${
                    selectedLocation === loc.id
                      ? 'border-olive-500 bg-olive-100 shadow-lg shadow-olive-600/10'
                      : 'border-sand-300 bg-white hover:border-olive-400 hover:shadow-md'
                  }`}
                  data-testid={`location-${loc.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-stone-800 mb-1">{loc.name}</h3>
                      <p className="text-stone-500 text-sm">
                        {loc.id === 'monopoli' ? txt.monopoliDesc : 
                         loc.id === 'polignano' ? txt.polignanoDesc : txt.fasanoDesc}
                      </p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedLocation === loc.id ? 'border-olive-500 bg-olive-500' : 'border-sand-400'
                    }`}>
                      {selectedLocation === loc.id && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="max-w-md mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Users className="text-olive-600" size={24} />
              <h2 className="text-2xl font-serif text-stone-800">{txt.whatTrip}</h2>
            </div>
            <p className="text-stone-500 text-sm mb-8">{txt.personalizeExp}</p>

            <div className="grid grid-cols-2 gap-3">
              {Object.values(GUEST_TYPES).map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedGuestType(type.id)}
                  className={`p-5 rounded-2xl border-2 text-center transition-all ${
                    selectedGuestType === type.id
                      ? 'border-olive-500 bg-olive-100 shadow-lg shadow-olive-600/10'
                      : 'border-sand-300 bg-white hover:border-olive-400 hover:shadow-md'
                  }`}
                  data-testid={`guest-type-${type.id}`}
                >
                  <span className="text-3xl mb-2 block">{type.icon}</span>
                  <span className="text-stone-800 font-medium">{type.label[language] || type.label.en}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="px-6 pb-8 safe-area-bottom">
        <button
          onClick={handleContinue}
          disabled={(step === 1 && !selectedLocation) || (step === 2 && !selectedGuestType)}
          className="w-full max-w-md mx-auto bg-olive-600 hover:bg-olive-700 disabled:bg-sand-300 disabled:text-sand-500 text-white py-4 rounded-2xl font-semibold transition-all flex items-center justify-center gap-2 shadow-lg shadow-olive-600/30"
          data-testid="onboarding-continue"
        >
          {step === 2 ? (
            <>
              <Sparkles size={18} />
              {txt.letsGo}
            </>
          ) : (
            <>
              {txt.continue}
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
